import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { QuietMind, SoundType } from '@hyggecards/design-system';

interface SoundContextType {
  soundEnabled: boolean;
  toggleSound: () => void;
  play: (type: SoundType) => void;
}

const SoundContext = createContext<SoundContextType | null>(null);

const SOUND_ENABLED_KEY = 'hyggecards-sound-enabled';

export function SoundProvider({ children }: { children: ReactNode }) {
  const [audio] = useState(() => new QuietMind({ enabled: false, volume: 0.3 }));
  const [soundEnabled, setSoundEnabled] = useState(() => {
    const stored = localStorage.getItem(SOUND_ENABLED_KEY);
    return stored === 'true';
  });

  // Initialize audio on first enable
  useEffect(() => {
    if (soundEnabled) {
      audio.init();
      audio.setEnabled(true);
    } else {
      audio.setEnabled(false);
    }
  }, [soundEnabled, audio]);

  // Sync dark mode with sound system by observing data-theme attribute
  useEffect(() => {
    const updateDarkMode = () => {
      const theme = document.documentElement.getAttribute('data-theme');
      audio.setDarkMode(theme === 'dark');
    };

    // Initial sync
    updateDarkMode();

    // Observe changes to data-theme attribute
    const observer = new MutationObserver(mutations => {
      for (const mutation of mutations) {
        if (mutation.attributeName === 'data-theme') {
          updateDarkMode();
        }
      }
    });

    observer.observe(document.documentElement, { attributes: true });

    return () => observer.disconnect();
  }, [audio]);

  // Persist preference
  useEffect(() => {
    localStorage.setItem(SOUND_ENABLED_KEY, String(soundEnabled));
  }, [soundEnabled]);

  // Cleanup on unmount
  useEffect(() => {
    return () => audio.dispose();
  }, [audio]);

  const toggleSound = useCallback(() => {
    setSoundEnabled(prev => {
      const newValue = !prev;
      if (newValue) {
        audio.init();
      }
      return newValue;
    });
  }, [audio]);

  const play = useCallback(
    (type: SoundType) => {
      if (soundEnabled) {
        audio.play(type);
      }
    },
    [soundEnabled, audio]
  );

  return (
    <SoundContext.Provider value={{ soundEnabled, toggleSound, play }}>
      {children}
    </SoundContext.Provider>
  );
}

export function useSound() {
  const context = useContext(SoundContext);
  if (!context) {
    throw new Error('useSound must be used within a SoundProvider');
  }
  return context;
}
